import React from "react";
import { createRoot, Root } from "react-dom/client";
import { Middleware } from "polymatic";

import { on, use } from "@piqnt/runtime-core/decorators";
import { SignalMiddleware } from "@piqnt/runtime-core/signals";

export type EmitFunction = (event: string, payload?: any) => void;

export interface MiddlewareContextValue<T> {
  context: T;
  emit: EmitFunction;
}

const MiddlewareContext = React.createContext<MiddlewareContextValue<any> | undefined>(undefined);

export function useMiddleware<T>(): MiddlewareContextValue<T> {
  const value = React.useContext(MiddlewareContext);
  if (value === undefined) {
    throw new Error("useMiddleware must be used within a MiddlewareContextProvider");
  }
  return value as MiddlewareContextValue<T>;
}

interface ReactMountOptions<C> {
  container: string | Element;
  render: (ctx: C) => React.ReactNode;
  shouldMount?: (ctx: C, element: Element) => boolean;
}

export class ReactMountMiddleware<C extends object> extends Middleware<C> {
  @use()
  signal = new SignalMiddleware();

  private root: Root | null = null;

  constructor(private options: ReactMountOptions<C>) {
    super();
  }

  @on("activate")
  protected handleActivate() {
    this.signal.effect(() => {
      const isContainerValid =
        typeof this.options.container === "string" ||
        (this.options.container instanceof Element && "appendChild" in this.options.container);

      if (!isContainerValid) {
        console.warn("Mount aborted - container is not valid", this.options.container);
        return;
      }

      let container: Element;
      if (typeof this.options.container === "string") {
        container = document.getElementById(this.options.container) as HTMLElement | null;
        if (!container) {
          console.warn("Mount aborted - container not found", this.options.container);
          return;
        }
      } else {
        container = this.options.container;
      }

      if (this.options.shouldMount && !this.options.shouldMount(this.context, container)) {
        console.warn("Mount aborted - shouldMount returned false", this.options.container);
        return;
      }

      const inUse = reactInternalProps(container);
      if (inUse) {
        console.warn("Mount aborted - container appears to be already used by React", inUse, this.options.container);
        return;
      }

      this.root = createRoot(container);

      this.root.render(
        <MiddlewareContext.Provider
          value={{
            context: this.context,
            emit: this.emit.bind(this),
          }}
        >
          {this.options.render(this.context)}
        </MiddlewareContext.Provider>,
      );

      return () => {
        console.log("Unmounting React root", container);
        this.root?.unmount();
        this.root = null;
      };
    });
  }
}

function reactInternalProps(target: Element) {
  if (!target || typeof target !== "object") {
    return null;
  }

  const matchingKeys: string[] = [];

  for (const key of Object.getOwnPropertyNames(target)) {
    if (key.toLowerCase().includes("react")) {
      matchingKeys.push(key);
    }
  }

  return matchingKeys.length > 0 ? matchingKeys : null;
}
